<script setup>
import { ref, computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';
import ContactAPI from 'dashboard/api/contacts';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const emit = defineEmits(['export']);

const { t } = useI18n();
const route = useRoute();

const dialogRef = ref(null);
const isDownloading = ref(false);

const segments = useMapGetter('customViews/getContactCustomViews');
const appliedFilters = useMapGetter('contacts/getAppliedContactFilters');
const uiFlags = useMapGetter('contacts/getUIFlags');
const isExportingContact = computed(() => uiFlags.value.isExporting);

const activeSegmentId = computed(() => route.params.segmentId);
const activeSegment = computed(() =>
  activeSegmentId.value
    ? segments.value.find(view => view.id === Number(activeSegmentId.value))
    : undefined
);

const getQueryPayload = () => {
  let query = { payload: [] };

  if (activeSegmentId.value && activeSegment.value) {
    query = activeSegment.value.query;
  } else if (Object.keys(appliedFilters.value).length > 0) {
    query = filterQueryGenerator(appliedFilters.value);
  }

  return {
    ...query,
    label: route.params.label || '',
  };
};

const exportContacts = async () => {
  const query = getQueryPayload();
  emit('export', query);
};

const downloadContacts = async () => {
  isDownloading.value = true;
  try {
    const query = getQueryPayload();
    const response = await ContactAPI.downloadContacts(query);
    // Crear un blob y descargar
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `contacts_${new Date().getTime()}.csv`);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.DOWNLOAD_SUCCESS')
    );
    dialogRef.value?.close();
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.DOWNLOAD_ERROR'));
  } finally {
    isDownloading.value = false;
  }
};

const handleDialogConfirm = async () => {
  await exportContacts();
  dialogRef.value?.close();
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.TITLE')"
    :description="
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.DESCRIPTION')
    "
    :show-confirm-button="false"
  >
    <div class="flex flex-col gap-4">
      <button
        :disabled="isExportingContact || isDownloading"
        class="w-full px-4 py-3 text-sm font-medium rounded-lg transition-colors bg-n-brand text-white hover:bg-n-brand/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        @click="downloadContacts"
      >
        <span v-if="!isDownloading" class="text-lg" />
        <span
          v-else
          class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
        />
        {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.DOWNLOAD') }}
      </button>

      <button
        :disabled="isExportingContact || isDownloading"
        class="w-full px-4 py-3 text-sm font-medium rounded-lg transition-colors bg-n-slate-3 text-n-slate-11 hover:bg-n-slate-4 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        @click="handleDialogConfirm"
      >
        <span v-if="!isExportingContact" class="text-lg" />
        <span
          v-else
          class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-n-slate-11 border-t-transparent"
        />
        {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.EMAIL') }}
      </button>
    </div>
  </Dialog>
</template>
